// Copyright 2026 Epic Digital Interactive Media LLC
// SPDX-License-Identifier: Apache-2.0

/**
 * @fileoverview Zod schema for the pullrequests VCA collection.
 *
 * Pull Requests and Merge Requests from any connected Git provider.
 * Provider-specific extras (e.g. GitHub's `auto_merge`, GitLab's `source_project_id`)
 * go in `providerData` so the table schema stays provider-agnostic.
 *
 * Identity tuple: (gitConnectionId, repoId, number)
 * ID prefix: prq_
 */

import { z } from 'zod'

export const pullrequestsSchema = z.object({
  id: z.string().max(100),
  orgId: z.string().max(100),
  workspaceId: z.string().max(100).optional(),

  // ── Git connection reference ──────────────────────────────────────────
  /** FK → git_connections.id */
  gitConnectionId: z.string().max(100),
  /** FK → codebases.id (FlowState-side codebase this PR belongs to) */
  codebaseId: z.string().max(100).optional(),

  // ── Identity tuple (provider-stable) ─────────────────────────────────
  /** Provider-side stable repository identifier (numeric or slug). */
  repoId: z.string().max(200),
  /** PR/MR number within the repository (provider-assigned, stable). */
  number: z.number().int(),

  // ── Core PR fields ────────────────────────────────────────────────────
  title: z.string().max(500),
  body: z.string().optional(),
  /** 'open' | 'closed' | 'merged' */
  state: z.enum(['open', 'closed', 'merged']),
  draft: z.boolean().default(false),

  // ── Branch refs ───────────────────────────────────────────────────────
  headRef: z.string().max(500),
  headSha: z.string().max(100),
  baseRef: z.string().max(500),
  baseSha: z.string().max(100),

  // ── Merge metadata ────────────────────────────────────────────────────
  /** null while not yet computed by the provider. */
  mergeable: z.boolean().nullable().optional(),

  // ── Diff stats ────────────────────────────────────────────────────────
  additions: z.number().int().optional(),
  deletions: z.number().int().optional(),
  changedFiles: z.number().int().optional(),

  // ── Participants ──────────────────────────────────────────────────────
  authorLogin: z.string().max(200),
  requestedReviewers: z.array(z.string()).optional(),
  assignees: z.array(z.string()).optional(),

  // ── Labels & milestones ───────────────────────────────────────────────
  labels: z.array(z.string()).optional(),
  /** Provider-side milestone identifier (nullable). */
  milestoneId: z.string().max(200).nullable().optional(),

  // ── Timestamps ───────────────────────────────────────────────────────
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  closedAt: z.string().datetime().nullable().optional(),
  mergedAt: z.string().datetime().nullable().optional(),
  /** Login of the user who merged the PR, if merged. */
  mergedBy: z.string().max(200).nullable().optional(),

  // ── Provider-specific extras ──────────────────────────────────────────
  /**
   * Arbitrary provider-specific data. Phase 6 sync adapters populate this
   * with provider-native fields that don't have a canonical column.
   */
  providerData: z.record(z.string(), z.unknown()).optional(),
})

export type PullrequestsInput = z.infer<typeof pullrequestsSchema>
